import {Nullable} from '../../app/TypeHelpers'
import {
    ACCESS_CONTROL_MANAGER,
    AVAILABLE_OR_NOT,
    CHARGE_UNIT,
    FACILITY_CATEGORY,
    PAYMENT_TYPE,
    RESERVATION_ACCEPTABILITY_FOR_RESIDENT,
    UNIT_OF_BUSINESS_SCHEDULE,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../CodeMaster'

export interface OptionSetting {
    optionId: number
    name: string
    charge: number
    chargeUnitTime: Nullable<number>
    chargeUnit: CHARGE_UNIT
    volumeMin: number
    volumeMax: number
    unitName: string
    paymentMethod: PAYMENT_TYPE
    appointRequired: VALID_OR_NOT
    sortOrder: number
}

export interface OvernightFacilitySetting {
    stayPossibleDateCount: number
    checkinTime: string
    checkoutTime: string
    stayCharge: number
}

export interface MaxReservationTimeByTimeZone {
    startTime: string
    endTime: string
    maxReservationTime: number
}

export interface BusinessSchedule {
    businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE
    dateStraddle: Nullable<VALID_OR_NOT>
    joinPrevDaysSchedule: Nullable<VALID_OR_NOT>
    startTime: Nullable<string>
    endTime: Nullable<string>
    charge: Nullable<number>
    chargeUnit: Nullable<CHARGE_UNIT>
    chargeUnitTime: Nullable<number>
    changedChargeApplyStartTime: Nullable<number>
    changedChargeApplyStartTimeUnit: Nullable<UNIT_TIME>
    changedCharge: Nullable<number>
    changedChargeUnit: Nullable<CHARGE_UNIT>
    changedChargeUnitTime: Nullable<number>
}

export interface DurationFacilitySetting {
    maxReservationTimeSetting: {
        allDayMaxReservationTime: Nullable<number>
        reservationTimeSections: Nullable<MaxReservationTimeByTimeZone[]>,
    }
    businessSchedules: BusinessSchedule[]
}

export interface FacilityInformation {
    facilityName: string
    facilityCategory: FACILITY_CATEGORY
    lotteryAvailability: AVAILABLE_OR_NOT
    facilityIntroductory: string
    facilityImages: FacilityImage[]
    overnightSetting: Nullable<OvernightFacilitySetting>
    durationSetting: Nullable<DurationFacilitySetting>
    lotterySetting: LotterySetting
}

export interface FacilityImage {
    uri: string
    displayOrder: number
}

export interface ClosedSetting {
    startDateTime: string,
    endDateTime: string,
}

export interface LotterySetting {
    facilityReserveLotteryStart: Nullable<string>
    facilityReserveLotteryEnd: Nullable<string>
    facilityReserveConfirmLimit: Nullable<string>
    facilityReserveLotteryMonth: Nullable<number>
    facilityReserveFasterPeriod: Nullable<number>
}

export default class FacilityForReservation {
    condominiumCd: string
    accessControlManager: Nullable<ACCESS_CONTROL_MANAGER>
    facilityId: number
    facilityName: string
    facilityCategory: FACILITY_CATEGORY
    residentReserve: RESERVATION_ACCEPTABILITY_FOR_RESIDENT
    cancelAvailability: AVAILABLE_OR_NOT
    cancelReceptionLimitDate: Nullable<number>
    cancelReceptionLimitTime: Nullable<string>
    lotteryAvailability: AVAILABLE_OR_NOT
    receptionStartMonth: Nullable<number>
    receptionStartDate: Nullable<number>
    receptionLimitDate: Nullable<number>
    receptionLimitTime: Nullable<string>
    facilityIntroductory: string
    stayAvailability: boolean
    overnightFacilitySetting: Nullable<OvernightFacilitySetting>
    durationFacilitySetting: Nullable<DurationFacilitySetting>
    lotterySetting: LotterySetting
    facilityOptions: OptionSetting[]
    facilityImages: FacilityImage[]
    closedSettings: ClosedSetting[]

    constructor(
        condominiumCd: string,
        accessControlManager: ACCESS_CONTROL_MANAGER | null,
        facilityId: number,
        facilityName: string,
        facilityCategory: FACILITY_CATEGORY,
        residentReserve: RESERVATION_ACCEPTABILITY_FOR_RESIDENT,
        cancelAvailability: AVAILABLE_OR_NOT,
        cancelReceptionLimitDate: number | null,
        cancelReceptionLimitTime: string | null,
        lotteryAvailability: AVAILABLE_OR_NOT,
        receptionStartMonth: number | null,
        receptionStartDate: number | null,
        receptionLimitDate: number | null,
        receptionLimitTime: string | null,
        facilityIntroductory: string,
        stayAvailability: boolean,
        overnightFacilitySetting: OvernightFacilitySetting | null,
        durationFacilitySetting: DurationFacilitySetting | null,
        lotterySetting: LotterySetting,
        facilityOptions: OptionSetting[],
        facilityImages: FacilityImage[],
        closedSettings: ClosedSetting[],
    ) {
        this.condominiumCd = condominiumCd
        this.accessControlManager = accessControlManager
        this.facilityId = facilityId
        this.facilityName = facilityName
        this.facilityCategory = facilityCategory
        this.residentReserve = residentReserve
        this.cancelAvailability = cancelAvailability
        this.cancelReceptionLimitDate = cancelReceptionLimitDate
        this.cancelReceptionLimitTime = cancelReceptionLimitTime
        this.lotteryAvailability = lotteryAvailability
        this.receptionStartMonth = receptionStartMonth
        this.receptionStartDate = receptionStartDate
        this.receptionLimitDate = receptionLimitDate
        this.receptionLimitTime = receptionLimitTime
        this.facilityIntroductory = facilityIntroductory
        this.stayAvailability = stayAvailability
        this.overnightFacilitySetting = overnightFacilitySetting
        this.durationFacilitySetting = durationFacilitySetting
        this.lotterySetting = lotterySetting
        this.facilityOptions = facilityOptions
        this.facilityImages = facilityImages
        this.closedSettings = closedSettings
    }

    static from(json: any): FacilityForReservation {
        let durationFacilitySetting: Nullable<DurationFacilitySetting>
        if (json.stayAvailability) {
            durationFacilitySetting = null
        } else {
            const facilitySetting = json.durationFacilitySetting

            durationFacilitySetting = {
                businessSchedules: facilitySetting.businessSchedules
                    .map((schedule: any) => {
                        const {
                            businessScheduleUnit,
                            chargeUnit,
                            changedChargeApplyStartTimeUnit,
                            dateStraddle,
                            joinPrevDaysSchedule,
                            changedChargeUnit,
                            ...rest
                        } = schedule
                        return {
                            ...rest,
                            businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE.codeToEnumConstants(businessScheduleUnit),
                            chargeUnit: chargeUnit ? CHARGE_UNIT.codeToEnumConstants(chargeUnit) : null,
                            changedChargeApplyStartTimeUnit: changedChargeApplyStartTimeUnit ?
                                UNIT_TIME.codeToEnumConstants(changedChargeApplyStartTimeUnit) : null,
                            changedChargeUnit:
                                changedChargeUnit ? CHARGE_UNIT.codeToEnumConstants(changedChargeUnit) : null,
                            dateStraddle: dateStraddle ? VALID_OR_NOT.codeToEnumConstants(dateStraddle) : null,
                            joinPrevDaysSchedule: joinPrevDaysSchedule ?
                                VALID_OR_NOT.codeToEnumConstants(joinPrevDaysSchedule) : null,
                        } as BusinessSchedule
                    }),
                maxReservationTimeSetting: facilitySetting.maxReservationTimeSetting,
            }
        }
        const facilityOptions: OptionSetting[] = json.facilityOptions.map((option: any) => {
            const {chargeUnit, paymentMethod, appointRequired, ...rest} = option
            return {
                ...rest,
                chargeUnit: CHARGE_UNIT.codeToEnumConstants(chargeUnit),
                paymentMethod: PAYMENT_TYPE.codeToEnumConstants(paymentMethod),
                appointRequired: VALID_OR_NOT.codeToEnumConstants(appointRequired),
            } as OptionSetting
        })

        return new FacilityForReservation(
            json.condominiumCd,
            json.accessControlManager ?
                ACCESS_CONTROL_MANAGER.codeToEnumConstants(json.accessControlManager) :
                null,
            json.facilityId,
            json.facilityName,
            FACILITY_CATEGORY.codeToEnumConstants(json.facilityCategoryId),
            RESERVATION_ACCEPTABILITY_FOR_RESIDENT.codeToEnumConstants(json.residentReserve),
            AVAILABLE_OR_NOT.codeToEnumConstants(json.cancelAvailability),
            json.cancelReceptionLimitDate,
            json.cancelReceptionLimitTime,
            AVAILABLE_OR_NOT.codeToEnumConstants(json.lotteryAvailability),
            json.receptionStartMonth,
            json.receptionStartDate,
            json.receptionLimitDate,
            json.receptionLimitTime,
            json.facilityIntroductory,
            json.stayAvailability,
            json.overnightFacilitySetting,
            durationFacilitySetting,
            json.lotterySetting,
            facilityOptions,
            json.facilityImages,
            json.closedSettings,
        )
    }

    static fromJsonOfInstance(json: any): FacilityForReservation {
        const facilityCategory: FACILITY_CATEGORY
            = FACILITY_CATEGORY.codeToEnumConstants(json.facilityCategory.value)

        let durationFacilitySetting: Nullable<DurationFacilitySetting>
        if (json.stayAvailability || json.durationFacilitySetting === null) {
            durationFacilitySetting = null
        } else {
            const facilitySetting = json.durationFacilitySetting

            durationFacilitySetting = {
                businessSchedules: facilitySetting.businessSchedules
                    .map((schedule: any) => {
                        const {
                            businessScheduleUnit,
                            chargeUnit,
                            changedChargeApplyStartTimeUnit,
                            changedChargeUnit,
                            dateStraddle,
                            joinPrevDaysSchedule,
                            ...rest
                        } = schedule
                        return {
                            ...rest,
                            businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE
                                .codeToEnumConstants(businessScheduleUnit.value),
                            chargeUnit: chargeUnit ? CHARGE_UNIT.codeToEnumConstants(chargeUnit.value) : null,
                            changedChargeApplyStartTimeUnit: changedChargeApplyStartTimeUnit ?
                                UNIT_TIME.codeToEnumConstants(changedChargeApplyStartTimeUnit.value) : null,
                            changedChargeUnit: changedChargeUnit ?
                                CHARGE_UNIT.codeToEnumConstants(changedChargeUnit.value)
                                : null,
                            dateStraddle: dateStraddle ? VALID_OR_NOT.codeToEnumConstants(dateStraddle.value) : null,
                            joinPrevDaysSchedule: joinPrevDaysSchedule ?
                                VALID_OR_NOT.codeToEnumConstants(joinPrevDaysSchedule.value) : null,
                        } as BusinessSchedule
                    }),
                maxReservationTimeSetting: facilitySetting.maxReservationTimeSetting,
            }
        }
        const facilityOptions: OptionSetting[] = json.facilityOptions.map((option: any) => {
            const {chargeUnit, paymentMethod, appointRequired, ...rest} = option
            return {
                ...rest,
                chargeUnit: CHARGE_UNIT.codeToEnumConstants(chargeUnit.value),
                paymentMethod: PAYMENT_TYPE.codeToEnumConstants(paymentMethod.value),
                appointRequired: VALID_OR_NOT.codeToEnumConstants(appointRequired.value),
            } as OptionSetting
        })

        const lotteryAvailability: AVAILABLE_OR_NOT =
            AVAILABLE_OR_NOT.codeToEnumConstants(json.lotteryAvailability.value)


        return new FacilityForReservation(
            json.condominiumCd,
            json.accessControlManager ?
                ACCESS_CONTROL_MANAGER.codeToEnumConstants(json.accessControlManager.value) :
                null,
            json.facilityId,
            json.facilityName,
            facilityCategory,
            RESERVATION_ACCEPTABILITY_FOR_RESIDENT.codeToEnumConstants(json.residentReserve.value),
            AVAILABLE_OR_NOT.codeToEnumConstants(json.cancelAvailability.value),
            json.cancelReceptionLimitDate,
            json.cancelReceptionLimitTime,
            lotteryAvailability,
            json.receptionStartMonth,
            json.receptionStartDate,
            json.receptionLimitDate,
            json.receptionLimitTime,
            json.facilityIntroductory,
            json.stayAvailability,
            json.overnightFacilitySetting,
            durationFacilitySetting,
            json.lotterySetting,
            facilityOptions,
            json.facilityImages,
            json.closedSettings,
        )
    }
}
